import axios from "axios";
import {BackgroundMetadata, PreviewMetadata} from "../common/ui/riamap/model";
import {ConfigurationSets, DBConnectionConfig, GeoprocessingServiceSettings} from "../settings/model";
import * as paths from "../paths";
import {getOidcBaseUrl, STUDIO_API_PATH, STUDIO_SERVICE_API_PATH} from "../paths";
import {
  AOIMonitor,
  AOINotificationArea,
  AOINotificationEvent,
  AOINotificationEventDetails
} from "../aoiNotifications/model";
import {AnalyticsRequest, AnalyticsResponse} from "../analytics/model";
import {AddResponse, Api} from "./InternalApi";
import {ControlRoomApiClass} from "./StudioInternalApi";
import {AccessRule, SpatialModel} from "../model";
import {FeatureTypePropertyType, FeatureTypesProperties,} from "../services/model";
import {stringToXml} from "../common/util/Util";
import {APOLLO_CATALOG_ROOT_FOLDER, BatchProperties, ImportedData, NewDataRoot, NewFileInfo} from "../data/model";
import {Role, User} from "../user/model";
import {WFSCapabilities} from "@luciad/ria/model/capabilities/WFSCapabilities.js";
import {
  localRepository,
  PREVIEW_BACKGROUND_BOUNDS,
  PREVIEW_BACKGROUND_CRS,
  PREVIEW_BACKGROUND_LAYERNAME,
  PREVIEW_BACKGROUND_SUBDOMAINS,
  PREVIEW_BACKGROUND_TYPE,
  PREVIEW_BACKGROUND_URL,
} from "../common/LocalRepository";
import {convertSecurityFormAccessRuleToAccessRule, PermissionsType, SecurityFormAccessRule} from "../security/model";
import {ITEM_HAS_NO_RIGHTS} from "../security/SecurityForm";
import {oidcProviderInfo} from "../oidc/startOidcProvider";
import {userManager} from "../oidc/utils/userManager";
import {ColorRamp, IchConfig} from "../styles/model";
import {Bounds} from "@luciad/ria/shape/Bounds.js";
import {createBounds} from "../common/ui/riamap/RiaApi";
import * as Oidc from "oidc-client";

export class ApolloApiClass extends ControlRoomApiClass implements Api {

  getStudioSettings = (): Promise<any> => {
    return axios.get(`${STUDIO_API_PATH}/configuration`)
        .then((r) => {
          return r;
        })
        .catch((error) => {
          throw error;
        });
  };

  saveStudioSettings = (settings: any) => {
    return axios.post(`${STUDIO_API_PATH}/configuration`, settings)
        .then(_ => null);
  };

  getStudioSettingsDescription = (): Promise<ConfigurationSets> => {
    return axios.get(`${STUDIO_API_PATH}/configuration/description`)
        .then((r) => {
          const config: ConfigurationSets = r.data;
          config.configurationSets.forEach(set => {
            set.id = encodeURIComponent(set.name);
          });
          return config;
        })
        .catch((error) => {
          throw error;
        });
  };

  getGeoprocessingServiceSettings = (): Promise<GeoprocessingServiceSettings> => {
    return axios.get(`${STUDIO_API_PATH}/configuration`)
        .then((r) => {
          const result = r.data.values as { [key: string]: string };
          return {
            geoprocessingServerUrl: result["desGeoprocessingEngineUrl"],
          };
        });
  };

  isGeoprocessingServiceUrlValid = (geoprocessingRootUrl: string): Promise<boolean> => {
    // For OIDC, we use axios interceptors to add the authorization header for all requests.
    // This request fails with CORS error because an authorization header is not expected.
    // For this one request, we are creating an instance of axios so that the interceptor will not be used.
    const unInterceptedAxiosInstance = axios.create();
    return unInterceptedAxiosInstance.get(`${geoprocessingRootUrl}/oapi-p/`, {
      withCredentials: false,
      timeout: 5000
    });
  };

  loadAOINotifications = (): Promise<AOIMonitor[]> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/notification/aoi?profile=full`,
    })
        .then((r) => {
          return (r.data || []) as AOIMonitor[];
        });
  }

  createAOINotification = (monitor: AOIMonitor): Promise<void> => {
    return axios({
      method: "POST",
      url: `${STUDIO_API_PATH}/notification/aoi`,
      data: {
        username: monitor.username,
        email: monitor.email,
        schedule: monitor.schedule,
        enabled: monitor.enabled,
        triggers: monitor.triggers,
      },
    })
        .then(_ => null);
  };

  deleteAOINotification = (monitorUserId: string): Promise<void> => {
    return axios({
      method: "DELETE",
      url: `${STUDIO_API_PATH}/notification/aoi/${monitorUserId}`,
    })
        .then(_ => null);
  };

  deleteAOINotificationArea = (monitor: AOIMonitor, trigger: AOINotificationArea): Promise<void> => {
    return axios({
      method: "DELETE",
      url: `${STUDIO_API_PATH}/notification/aoi/${monitor.id}/trigger/${trigger.id}`,
    })
        .then(_ => null);
  };

  updateAOINotification = (monitor: AOIMonitor, notificationArea?: Partial<AOINotificationArea>): Promise<void> => {
    return Promise.resolve()
        .then(() => {
          if (notificationArea != null && !notificationArea?.id) {
            //Create aoi
            return axios({
              method: "POST",
              url: `${STUDIO_API_PATH}/notification/aoi/${monitor.id}/trigger`,
              data: {
                name: notificationArea.name,
                srs: notificationArea.srs,
                geomWkt: notificationArea.geomWkt,
              },
            });
          } else if (notificationArea != null && notificationArea?.id) {
            //update trigger
            return axios({
              method: "PATCH",
              url: `${STUDIO_API_PATH}/notification/aoi/${monitor.id}/trigger/${notificationArea.id}`,
              data: {
                name: notificationArea.name,
                srs: notificationArea.srs,
                geomWkt: notificationArea.geomWkt,
              },
            });
          } else {
            //update monitor
            const trigs = monitor.triggers;
            trigs.forEach((trigger) => {
              delete trigger["id"];
              delete trigger["monitorUserId"];
            });

            return axios({
              method: "PATCH",
              url: `${STUDIO_API_PATH}/notification/aoi/${monitor.id}`,
              data: {
                email: monitor.email,
                username: monitor.username,
                schedule: monitor.schedule,
                enabled: monitor.enabled,
                triggers: trigs,
              },
            });
          }
        })
        .then(_ => null);
  };

  getMetadataParsers = () => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/data/metadata-parsers`,
    }).then(r => {
      return r.data;
    });
  }

  getNotificationEvents = (monitorUserId: string, areaId: string): Promise<AOINotificationEvent[]> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/notification/aoi/${monitorUserId}/event?whatToInclude=all`,
    }).then(r => {
      return r.data.filter(a => a.triggerId === areaId) || [];
    });
  };

  getNotificationEventDetails = (eventId: string, monitorUserId: string): Promise<AOINotificationEventDetails> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/notification/aoi/${monitorUserId}/event/${eventId}`,
    }).then(r => {
      return r.data;
    });
  };

  updateNotificationEvent = (event: AOINotificationEvent): Promise<void> => {
    return axios({
      method: "PATCH",
      url: `${STUDIO_API_PATH}/notification/aoi/${event.monitorUserId}/event`,
      data: {
        "readStatus": event.hasBeenRead,
        "eventIds": [
          event.id
        ]
      }
    }).then(_ => null);
  };

  deleteNotificationEvent = (monitorUserId: string, eventIds: string[]): Promise<void> => {
    return axios({
      method: "DELETE",
      url: `${STUDIO_API_PATH}/notification/aoi/${monitorUserId}/event`,
      data: eventIds,
    }).then(_ => null);
  };

  getPreviewBackground = (): BackgroundMetadata => {
    if (localRepository != null) {
      let bounds: Bounds = null;
      try {
        const boundsA = localRepository.getParameter(PREVIEW_BACKGROUND_BOUNDS).split(',');
        if (boundsA.length === 4) {
          bounds = createBounds({
            x: Number(boundsA[0]),
            width: Number(boundsA[1]),
            y: Number(boundsA[2]),
            height: Number(boundsA[3])
          });
        }
      } catch (_e) {
        bounds = null;
      }

      const bgMetadata: BackgroundMetadata = {
        type: localRepository.getParameter(PREVIEW_BACKGROUND_TYPE),
        crs: localRepository.getParameter(PREVIEW_BACKGROUND_CRS),
        bounds
      };
      if (localRepository.getParameter(PREVIEW_BACKGROUND_URL) !== null &&
          localRepository.getParameter(PREVIEW_BACKGROUND_URL) !== "undefined") {
        switch (bgMetadata.type) {
        case "wms":
          bgMetadata.url = localRepository.getParameter(PREVIEW_BACKGROUND_URL);
          bgMetadata.layerName = localRepository.getParameter(PREVIEW_BACKGROUND_LAYERNAME);
          bgMetadata.subdomains = null;
          break;
        case "urlTileSet":
          bgMetadata.url = localRepository.getParameter(PREVIEW_BACKGROUND_URL);
          bgMetadata.subdomains = bgMetadata.url.includes("{s}") ?
                                  localRepository.getParameter(PREVIEW_BACKGROUND_SUBDOMAINS).split(",") : null;
          bgMetadata.layerName = null;
          break;
        }
      }
      return bgMetadata;
    }
    return null;
  }

  getAnalytics = (request: AnalyticsRequest): Promise<AnalyticsResponse[]> => {
    return axios({
      method: "POST",
      url: `${STUDIO_API_PATH}/analytics`,
      data: request,
    })
        .then(r => {
          return r.data;
        });
  }

  saveSLDStyle = (title: string, sld: string, keywords?: string[]): Promise<any> => {
    const formData = new FormData();
    formData.append("files", new File([new Blob([sld])], `${title}.sld`));
    if (keywords?.length > 0) {
      formData.append("keywords", [...new Set(keywords)].join(','));
    }

    return axios({
      method: "POST",
      url: `${STUDIO_SERVICE_API_PATH}/upload/v2/files`,
      data: formData,
      headers: {"Content-Type": "application/octet-stream"}
    }).then(r => {
      return r.data;
    });
  }

  uploadVirtualFile = (filename: string, data: string, options?: { [key: string]: any }): Promise<any> => {
    const formData = new FormData();
    formData.append("files", new File([new Blob([data])], `${filename}`));
    for (const key in options) {
      if (options.hasOwnProperty(key)) {
        formData.append(key, JSON.stringify(options[key]));
      }
    }

    return axios({
      method: "POST",
      url: `${STUDIO_API_PATH}/upload/files`,
      data: formData,
      headers: {"Content-Type": "application/octet-stream"}
    }).then(r => {
      return r.data;
    });
  }

  getWmsOptions = (): any => {
    if (oidcProviderInfo?.enabled) {
      return {
        requestHeaders: {
          Authorization: "Bearer " + this.getOidcTokenValue()
        },
        credentials: true
      };
    } else {
      return {};
    }
  }

  isOidcAdminRole = (): boolean => {
    // Check for keycloak realm_roles first
    const realmRoles: string[] = this.getKeycloakOidcRolesFromToken();
    if (this.adminRoleExists(realmRoles)) {
      return true;
    }
    // check for roles from non-keycloak oidc/oauth2 provider
    const roles: string[] = this.getNonKeycloakOidcRolesFromToken();
    return (this.adminRoleExists(roles));
  }

  getOidcTokenValue = (): string => {
    const jsonValue = this.getOidcTokenAsJson();
    if (jsonValue) {
      const token = JSON.parse(jsonValue)
      if (token?.hasOwnProperty("access_token")) {
        return token.access_token;
      }
    }
    return "";
  }

  // Keycloak is configured to have roles in realm_access
  // Decode base64 encoded JWT and parse as json to get to realm_access.roles
  // The roles array contains the allowed roles for the OpenID user logged in.
  getKeycloakOidcRolesFromToken = (): string[] => {
    const json = this.getOidcTokenValue();
    if (json !== "") {
      const jsonValue = JSON.parse(window.atob(json.split('.')[1]))
      if (jsonValue?.hasOwnProperty("realm_access")) {
        return jsonValue.realm_access.roles;
      }
    }
    return [];
  }

  // Non-keycloak roles should be at top level of JWT claim.
  // Decode base64 encoded JWT and parse as json to get roles.
  // The roles array contains the allowed roles for the OpenID user logged in.
  getNonKeycloakOidcRolesFromToken = (): string[] => {
    const jsonValue = this.getOidcTokenAsJson();
    if (jsonValue) {
      const token = JSON.parse(window.atob(jsonValue.split('.')[1]))
      if (token?.hasOwnProperty("roles")) {
        return token.roles;
      }
    }
    return [];
  }

  isOidcAuthenticated = (): boolean => {
    return this.getOidcTokenValue() !== "";
  }

  replaceOidcUrlWithLoginUrl = () => {
    //window.history.go(-window.history.length);
    // If press back button after logging out, there could still be the state query in the url
    // so remove everything after the ? and replace with login
    window.location.href = getOidcBaseUrl();
    window.history.replaceState(null, "", window.location.href);
  }

  logoutOidcProvider = async () => {
    try {
      await userManager.getUser().then((user: Oidc.User) => {
        if (user != null) {
          userManager.signoutRedirect({
            id_token_hint: user?.id_token,
            post_logout_redirect_uri: getOidcBaseUrl() + "#/login",
            state: user?.state
          }).catch((error)=> {
            throw new Error("Failed to log out:" + error);
          });
        }
        userManager.clearStaleState();
        window.sessionStorage.clear();
      });
    } catch (error) {
      throw new Error("Error occurred while logging out : " + error);
    }
  }

  private getOidcTokenAsJson() {
    const key = "oidc.user:" + oidcProviderInfo.authorizationEndpoint + ":" + oidcProviderInfo.clientId;
    return window.sessionStorage.getItem(key);
  }

  private adminRoleExists = (roles:string[]): boolean => {
    if (roles.length > 0) {
      if (roles.find((str) => str === "ADMIN" || str === "STUDIOMANAGER")) {
        return true;
      }
    }
    return false;
  }

  getDataDetails = (id: string): any => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/data/${id}`,
    }).then(r => {
      return r.data;
    });
  }

  batchUpdate = (id: string,
                 batchProperties: BatchProperties,
                 applyToFolders: boolean,
                 applyToImages: boolean,
                 applyToVectors: boolean,
                 applyToPointClouds: boolean,
                 applyToPanoramics: boolean,
                 applyToMeshes: boolean,
                 applyToDocuments: boolean,
                 directChildrenOnly: boolean) => {
    return axios.post(`${STUDIO_API_PATH}/data/batch-update`, {
      updates: [
        {
          ids: [id],
          applyToChildren: !directChildrenOnly,
          categories: {
            "folders": applyToFolders,
            "images": applyToImages,
            "vectors": applyToVectors,
            "pointClouds": applyToPointClouds,
            "documents": applyToDocuments,
            "panoramics": applyToPanoramics,
            "meshes": applyToMeshes,
          },
          ...batchProperties,
        }
      ]
    })
        .then(_ => null);
  };

  private getRootFolder = (): Promise<any> => {
    const folders = [];
    folders.push(APOLLO_CATALOG_ROOT_FOLDER);
    return axios.get(`${STUDIO_API_PATH}/folder/detailed?foldersOnly=true&page=1&pageSize=65536`)
        .then(r => {
          r.data.results.forEach((folder) => {
            if (folder.path.startsWith("folder=")) {
              folders.push({
                'id': folder.id,
                'title': folder.name,
                'path': folder.path,
                'parent': APOLLO_CATALOG_ROOT_FOLDER
              });
            }
          });
          return folders;
        })
        .catch((error) => {
          throw error;
        });
  }

  private getFolder = (folderId: string): Promise<any> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/folder/detailed?foldersOnly=true&page=1&pageSize=65536&parentId=${folderId}`,
    }).then(r => {
      return r.data.results;
    });
  }

  private getAllFldrs = (folderId?: string): Promise<any> => {
    return this.getRootFolder().then((rootFolder) => {
      const folders = rootFolder;
      return this.getPath(folderId).then((path) => {
        const parentsPromise = [];
        const parents = [];

        if (path.length > 1) {
          path.forEach((folder) => {
            if (folder !== "ROOT") {
              parentsPromise.push(this.getFolder(folder));
              parents.push(folder);
            }
          });
        }
        if (parentsPromise.length > 0) {
          return this.getChildFldrs(folderId, folders, parents, parentsPromise);
        } else {
          return folders;
        }
      });
    });
  }

  private getChildFldrs = (folderId: string, folders: any[], parents: string[],
                           parentsPromise: Promise<any>[]): Promise<any> => {
    return Promise.allSettled(parentsPromise).then((response) => {
      const results = response.filter((res) => res.status === 'fulfilled') as PromiseFulfilledResult<any>[];

      for (let i = 0; i < results.length; i++) {
        results[i].value.forEach((data) => {
          if (data.path.startsWith("folder=")) {
            if (folders.findIndex((folder) => folder.id === data.id) < 0) {
              folders.push({
                id: data.id,
                title: data.title ? data.title : data.name,
                path: data.path,
                parent: folders.find(p => p.id === parents[i]),
              });
            }
          }
        });
      }
      return folders;
    });
  }

  getPath = (folderId: string): Promise<any> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/folder/parents?id=${folderId}`,
    }).then(r => {
      return r.data;
    }).catch(_r => {
      const urlParams = new URLSearchParams(document.URL.substring(document.URL.lastIndexOf('?')));
      if (urlParams.has('folder')) {
        urlParams.delete('folder');
        window.history.replaceState({}, '',
            `${location.pathname}${Array.from(urlParams).length > 0 ? "?" + urlParams.toString() : ""}`
        );
        window.location.reload();
      }
      return null;
    });
  }

  getAllFolders = (folderId?: string): Promise<any> => {
    if (!folderId || folderId === APOLLO_CATALOG_ROOT_FOLDER.id) {
      return this.getRootFolder();
    } else {
      return this.getAllFldrs(folderId);
    }
  }

  getFolders = (folderId?: string): Promise<any> => {
    if (!folderId || folderId === APOLLO_CATALOG_ROOT_FOLDER.id) {
      return this.getRootFolder();
    } else {
      return this.getFolder(folderId);
    }
  }

  createFolder = (parentId: string, name: string): Promise<void> => {
    return axios({
      method: "POST",
      url: `${STUDIO_API_PATH}/folder`,
      data: {
        folderName: name,
        parent: parentId !== "0" ? parentId : null
      }
    }).then(_ => null);
  }

  isFolder = (folderId: string): Promise<boolean> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/folder?page=1&pageSize=1&parentId=${folderId}`
    }).then(_r => {
      return true;
    }).catch(_r => {
      return false;
    })
  }

  getImportedDataPreviewMetadata = (data: SpatialModel) => {
    if (data.type === "folder") {
      return Promise.resolve({
        previewable: false,
        shouldForceToPreview: false,
        georeferenced: true,
        empty: true,
      });
    }
    return axios
        .get(`${STUDIO_SERVICE_API_PATH}${paths.PREVIEW_PATH}/preview-metadata/imported-data/${data.id}`)
        .then((response) => response.data) as Promise<PreviewMetadata>;
  };

  getFeatureTypesProperties = (capabilities: WFSCapabilities): Promise<FeatureTypesProperties> => {
    return Promise.resolve()
        .then(() => {
          const operation = capabilities.operations.find(o => o.name === "DescribeFeatureType");
          if (!operation.supportedFormats.some(f => f === "text/xml; subtype=gml/3.1.1")) {
            return {} as FeatureTypesProperties;
          }
          const request = (operation as any)?.supportedRequests.find(r => r.method === "GET");
          if (!request) {
            return {} as FeatureTypesProperties;
          }
          return axios.get(`${request.url}service=WFS&request=DescribeFeatureType&version=2.0.0`,
              {withCredentials: false})
              .then((r) => {
                const result: FeatureTypesProperties = {};
                const xml = stringToXml(r.data);
                const featureTypesNames = capabilities.featureTypes.map(t => t.name);
                const schemas = this.getSchemas(xml, featureTypesNames);

                if (schemas.length > 0) {
                  return Promise.allSettled(schemas.map((url) => axios.get(url)))
                      .then((response) => {
                        const results = response.filter(
                            (res) => res.status === 'fulfilled') as PromiseFulfilledResult<any>[];
                        if (results.length > 0) {
                          results.forEach((xmlDoc) => {
                            this.processDescribeFeatureType(stringToXml(xmlDoc.value.data), featureTypesNames, result);
                          })
                          return result;
                        } else {
                          throw new Error(
                              "ApolloAPI: Invalid WFS document (unable to read additional Schema documents).");
                        }
                      })
                      .catch(() => {
                        throw new Error("ApolloAPI: Invalid WFS document.");
                      });
                } else {
                  this.processDescribeFeatureType(xml, featureTypesNames, result);
                  return result;
                }
              });
        });
  };

  private getSchemas = (xml, featureTypesNames) => {
    const schemas = [];
    featureTypesNames.forEach((featureName) => {
      const schemaImport = xml.querySelector(
          `schema > import[namespace='${featureName.replace(/^.*:/, "")}']`);
      if (schemaImport) {
        const schemaLocation = schemaImport.getAttribute('schemaLocation');
        if (schemaLocation) {
          schemas.push(schemaLocation);
        }
      }
    });
    return schemas;
  }

  private processDescribeFeatureType = (xml: Document, featureTypesNames, result) => {
    featureTypesNames.forEach((featureTypeName) => {
      let tnsName = featureTypeName.replace(/^.*:/, "tns:");
      tnsName = tnsName.endsWith("Type") ? tnsName : `${tnsName}Type`;
      const elementNameType = xml.querySelector(`schema > element[type='${tnsName}']`);
      if (!elementNameType) {
        return new Error(
            "{\"id\":\"studio.style-editor.wfs-invalid-document\", \"defaultMessage\":\"Invalid WFS document.\"}");
      }
      if (!result[featureTypeName]) {
        result[featureTypeName] = [];
      }
      const elementName = elementNameType.getAttribute("name") + "Type";
      Array.from(xml.querySelectorAll(`schema > *[name='${elementName}'] element`)).forEach((p) => {
        const newElement = {
          name: p.getAttribute("name"),
          type: ApolloApiClass.mapFeatureTypePropertyType(p.getAttribute("type")),
        };
        if (!result[featureTypeName].includes(newElement)) {
          result[featureTypeName].push(newElement);
        }
      });
    });

  }

  addImportedData = (file: NewFileInfo) => {
    return axios
        .post(`${STUDIO_API_PATH}/data`, file)
        .then((r) => r.data.data)
        .catch(this.notifyErrorHandlers) as Promise<ImportedData[]>;
  }

  getCrawlAccessRules = (): Promise<AccessRule[]> => {
    return axios.get(`${STUDIO_API_PATH}/configuration`)
        .then((r) => {
          const settings = r.data.values;
          return JSON.parse(settings["crawlAccessRules"]);
        })
        .catch(this.notifyErrorHandlers) as Promise<AccessRule[]>;
  }

  getAllRolesWithDefaultAccessRules = (): Promise<AccessRule[]> => {
    // Called from Setting->Crawling Defaults page
    return this.getAllRoles()
        .then((roles: Role[]) => {
          return roles.map((n) => {
            return {
              role: n.role,
              permissions: [PermissionsType.READ]
            } as AccessRule
          })
        });
  }

  getAccessRules = (id: string): Promise<AccessRule[]> => {
    if (id === undefined || id === "") {
      const noAccessRights: AccessRule[] = [{
        role: ITEM_HAS_NO_RIGHTS,
        permissions: [PermissionsType.READ],
      }];
      return Promise.resolve(noAccessRights);
    } else {
      return axios
          .get(`${STUDIO_API_PATH}/` + "data/" + id + "/access-rules")
          .then((r) => {
            return r.data.accessRules;
              }
          )
          .catch(this.notifyErrorHandlers);
    }
  }

  deleteAccessRules = (id: string, deletedRules: SecurityFormAccessRule[]) => {
    const resultPromises: Promise<any>[] = this.deleteDataItemAccessRules(id, deletedRules);
    return Promise.all(resultPromises).then(() => Promise.resolve());
  }

  private deleteDataItemAccessRules(id: string, accessRoles: SecurityFormAccessRule[]) {
    //Create an array of promises.
    return accessRoles.map(n => {
      if (n.applyToChildren) {
        return axios.delete(
            `${STUDIO_API_PATH}/` + "data/" + id + "/bulk-access-rules/" + n.id)
            .catch(this.notifyErrorHandlers);
      } else {
        return axios.delete(
            `${STUDIO_API_PATH}/` + "data/" + id + "/access-rules/" + n.id)
            .catch(this.notifyErrorHandlers);
      }
    });
  }

  setAccessRules = (id: string, accessRules: SecurityFormAccessRule[]) => {
    const resultPromises: Promise<any>[] = this.setDataItemAccessRules(accessRules, id);
    return Promise.all(resultPromises).then(() => Promise.resolve());
  }

  private setDataItemAccessRules(accessRules: SecurityFormAccessRule[], id: string) {
    return accessRules.map(n => {
      if (n.isNew) {
        const currentAccess = convertSecurityFormAccessRuleToAccessRule(n);

        if (n.applyToChildren) {
          return this.createAccessPermissionPromise(
              `${STUDIO_API_PATH}/` + "data/" + id + "/bulk-access-rules", currentAccess);
        } else {
          return this.createAccessPermissionPromise(
              `${STUDIO_API_PATH}/` + "data/" + id + "/access-rules", currentAccess);
        }
      } else {
        const currentAccess = convertSecurityFormAccessRuleToAccessRule(n);

        if (n.applyToChildren) {
          return this.createAccessPermissionPromise(
              `${STUDIO_API_PATH}/` + "data/" + id + "/bulk-access-rules", currentAccess);
        } else {
          return this.updateAccessPermissionPromise(
              `${STUDIO_API_PATH}/` + "data/" + id + "/access-rules/" + n.id, currentAccess);
        }
      }
    });
  }

  createAccessPermissionPromise = (url: string, payload: any): Promise<void> => {
    return axios
        .post(url, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }
  updateAccessPermissionPromise = (url: string, payload: any): Promise<void> => {
    return axios
        .put(url, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  isUserManagementActive = (): Promise<any> => {
    let isActive = true;
    this.getActuator("env").then((properties) => {
      const envObject = properties.propertySources.find(
          x => x.name.startsWith("Config resource") && x.name.endsWith("application-fusion.apollo.properties'"));
      isActive = envObject.properties["fusion.accessControl.enabled"].value === "true";
      return Promise.resolve(isActive);
    }).catch(() => {
      return Promise.resolve(isActive);
    });
    return Promise.resolve(isActive);
  }

  createRole = (role: Role): Promise<any> => {
    const payload: any = {
      "role": role.role,
      "description": role.description
    };
    return axios.post(`${STUDIO_API_PATH}/roles`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  getRole = (role: Role): Promise<any> => {
    return axios.get(`${STUDIO_API_PATH}/roles/${role.role}`)
        .then(r => {
          return r.data
        })
        .catch(this.notifyErrorHandlers);
  }

  getUser = (user: User): Promise<any> => {
    return axios.get(`${STUDIO_API_PATH}/users/${user.username}`)
        .then(r => {
          return r.data
        })
        .catch(this.notifyErrorHandlers);
  }
  deleteRole = (role: Role): Promise<any> => {
    return axios.delete(`${STUDIO_API_PATH}/roles/${role.role}`)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  deleteUser = (user: User): Promise<any> => {
    return axios.delete(`${STUDIO_API_PATH}/users/${user.username}`)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  createUser = (user: User): Promise<any> => {
    const payload: any = {
      "username": user.username,
      "password": user.userAuthCredentials,
      "roles": user.roles,
      "enabled": user.enabled
    };
    return axios.post(`${STUDIO_API_PATH}/users`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  updateRole = (role: Role): Promise<any> => {
    const payload: any = {
      "role": role.role,
      "description": role.description,
    };
    return axios.patch(`${STUDIO_API_PATH}/roles/${role.role}`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }
  updateUser = (user: User): Promise<any> => {
    const payload: any = {
      "username": user.username,
      "password": user.userAuthCredentials,
      "roles": user.roles,
      "enabled": user.enabled
    };
    return axios.patch(`${STUDIO_API_PATH}/users/${user.username}`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }
  getAllUsers = (): Promise<User[]> => {
    return axios
        .get(`${STUDIO_API_PATH}/` + "users")
        .then((r) => {
          const users = r.data;
          const usersList: User[] = users.map(n => {
            const user: User = {
              username: n.username,
              roles: n.roles,
              enabled: n.enabled
            }
            return user;
          });
          return usersList;
        }).catch(this.notifyErrorHandlers);
  }

  getCustomProperties = (): Promise<any> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/data/custom-properties`
    }).then(r => {
      return r.data;
    }).catch(_r => {
      return null;
    })
  }

  getAllRoles = (): Promise<Role[]> => {
    return axios
        .get(`${STUDIO_API_PATH}/` + "roles")
        .then((r) => {
          const returnedRoles = r.data;
          const roleList: Role[] = returnedRoles.map(n => ({role: n.role, description: n.description}))
          return roleList;
        }).catch(this.notifyErrorHandlers);
  }

  downloadApolloMetadata = (id: string) => {
    let attachmentUrl = `${STUDIO_API_PATH}/attachments/${id}/metadata`;
    axios
        .get(attachmentUrl)
        .then((r) => {
          const blob = new Blob([r.data], {type: 'text/xml'});
          attachmentUrl = URL.createObjectURL(blob);
          window.open(attachmentUrl);
          URL.revokeObjectURL(attachmentUrl); //Releases the resources
        });
  }

  getImageChainColorRamps = (): Promise<ColorRamp[]> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/styles/imagine/describe/colorramps`
    }).then(r => {
      const colorRamps: ColorRamp[] = r.data;
      colorRamps.sort((a, b) => a.name.localeCompare(b.name));
      return colorRamps;
    });
  }

  getImageChainProviders = (): Promise<IchConfig> => {
    return axios({
      method: "GET",
      url: `${STUDIO_API_PATH}/styles/imagine/describe/providers`
    }).then(r => {
      const ichConfig: IchConfig = r.data;
      ichConfig.imageChainProviders.providers[0].imageChains.sort(
          (a, b) => {
            if (a.title > b.title) {
              return 1
            } else if (b.title > a.title) {
              return -1
            } else {
              return 0
            }
          });
      ichConfig.imageChainProviders.providers[0].imageChains.forEach((provider) => {
        provider.parameters.sort((a, b) => {
          if (a.name > b.name) {
            return 1
          } else if (b.name > a.name) {
            return -1
          } else {
            return 0
          }
        })
      });
      return ichConfig;
    });
  }

  private static mapFeatureTypePropertyType(type: string): FeatureTypePropertyType {
    switch (type) {
    case "xsd:int":
    case "xsd:long":
    case "xsd:decimal":
    case "xsd:short":
    case "xsd:float":
    case "xsd:double":
      return FeatureTypePropertyType.NUMERIC;
    case "xsd:string":
      return FeatureTypePropertyType.STRING;
    default:
      return null;
    }
  }

  getDatabaseConnections = (): Promise<DBConnectionConfig[]> => {
    return Promise.resolve([]);
  }

  addFiles = (files: NewFileInfo[]) => {
    const allFiles = files.map((file) => axios
        .post(`${STUDIO_API_PATH}/data`, file)
        .then((r) => r.data.data)
        .catch(this.notifyErrorHandlers) as Promise<AddResponse>);

    return Promise.all((allFiles))
        .then((r) => r[0])
        .catch(this.notifyErrorHandlers) as Promise<AddResponse>;
  }

  createDataRoot = (dataRoot: NewDataRoot) => {
    return axios
        .post(`${STUDIO_API_PATH}/data-roots`, dataRoot)
        .then((r) => r.data.data)
        .catch(this.notifyErrorHandlers) as Promise<AddResponse>;
  }

  updateProperties = (updatedData: ImportedData): Promise<any> => {
    const update = {
      title: updatedData.title,
      abstractText: updatedData.abstractText,
      keywords: updatedData.keywords,
      customProperties: updatedData.customProperties,
    }
    return axios.patch(`${STUDIO_API_PATH}/data/${updatedData.id}`, update)
        .then((r) => r.data.data)
        .catch(this.notifyErrorHandlers);
  }

}

export const ApolloApi = new ApolloApiClass(STUDIO_SERVICE_API_PATH, STUDIO_API_PATH);